import React, { useState } from "react";
import InboxView from "../../components/InboxView";
import { circlesApi } from "./circlesApi";
import { Modal, StatusBadge, inr, shortDate, useBusy, useCirclesList } from "./circlesShared";
import "../../assets/css/PageHeading.css";

const TABS = [
  { id: "Pending", label: "To review", tone: "#d97706" },
  { id: "Approved", label: "Approved (to pay)", tone: "#16a34a" },
  { id: "Paid", label: "Paid", tone: "#1e3a8a" },
  { id: "Rejected", label: "Rejected", tone: "#dc2626" },
  { id: "Reversed", label: "Reversed", tone: "#64748b" },
];

const rateText = (c) => (c.Kind === "App registration" ? "App registration" : `L${c.Level} · ${c.Commission_Type === "Fixed" ? inr(c.Rate) : `${c.Rate}%`}`);

function RejectModal({ item, onClose, onDone }) {
  const [note, setNote] = useState("");
  const { busy, error, run } = useBusy();
  const submit = () =>
    run(async () => {
      await circlesApi.decideCommission(item.Commission_Id, "Rejected", note);
      onDone();
    });
  return (
    <Modal
      title={`Reject commission for ${item.Agent_Name}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--danger" disabled={busy}>
            Reject
          </button>
        </>
      }
    >
      <p className="wl-muted">
        {inr(item.Amount)} · {item.Customer} · {item.Project_Name}
      </p>
      <label className="wl-field">
        <span>Reason</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

export default function CirclesCommissions() {
  const [tab, setTab] = useState("Pending");
  const [rejecting, setRejecting] = useState(null);
  const [notice, setNotice] = useState(null);
  const { busy, error: actionError, run } = useBusy();
  const { rows, extra, loading, error, reload } = useCirclesList(() => circlesApi.commissions({ status: tab }), [tab]);

  const approve = (c) =>
    run(async () => {
      await circlesApi.decideCommission(c.Commission_Id, "Approved");
      setNotice({ type: "success", text: `${inr(c.Amount)} approved for ${c.Agent_Name}.` });
      reload();
    });

  const actions = (c) =>
    c.Status === "Pending" ? (
      <div className="cx-actions">
        <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" disabled={busy} onClick={() => approve(c)}>
          Approve
        </button>
        <button type="button" className="wl-btn wl-btn--sm" onClick={() => setRejecting(c)}>
          Reject
        </button>
      </div>
    ) : c.Status === "Approved" ? (
      <button type="button" className="wl-btn wl-btn--sm" onClick={() => setRejecting(c)}>
        Reject
      </button>
    ) : (
      <span className="wl-sub">{c.Needs_Adjustment ? "Sale reversed after payout: adjust the next payout" : c.Note || "—"}</span>
    );

  return (
    <div className="p-2 p-md-3">
      <div className="co-page-head">
        <h2 className="co-page-head__title">Circles Commissions</h2>
      </div>
      <p className="cx-hint mb-2">Commissions are created when a Digix Leads lead is marked Converted with its deal amount, or an app registration is confirmed.</p>
      <InboxView
        tabs={TABS}
        counts={extra.counts || {}}
        tab={tab}
        onTab={setTab}
        onRefresh={reload}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error || actionError}
        loading={loading}
        rows={rows}
        rowKey={(c) => c.Commission_Id}
        empty="No commissions here."
        columns={[
          { key: "date", label: "Date", render: (c) => shortDate(c.Created_On) },
          { key: "agent", label: "Agent", render: (c) => c.Agent_Name },
          { key: "sale", label: "Customer · project", render: (c) => (<><div>{c.Customer || "—"}</div><div className="wl-sub">{[c.Project_Name, c.Tenant_Name].filter(Boolean).join(" · ")}</div></>) },
          { key: "rate", label: "For", render: rateText },
          { key: "deal", label: "Deal", render: (c) => inr(c.Deal_Amount) },
          { key: "amount", label: "Amount", render: (c) => <strong>{inr(c.Amount)}</strong> },
          { key: "status", label: "Status", render: (c) => <StatusBadge status={c.Status} /> },
          { key: "actions", label: "Actions", className: "wl-actions-col", render: actions },
        ]}
        mobileCard={(c) => ({
          title: `${inr(c.Amount)} · ${c.Agent_Name}`,
          meta: [{ label: c.Status, tone: c.Status === "Pending" ? "warning" : c.Status === "Approved" || c.Status === "Paid" ? "success" : "neutral" }],
          fields: [
            { label: "Customer", value: c.Customer },
            { label: "Project", value: c.Project_Name },
            { label: "For", value: rateText(c) },
            { label: "Deal", value: c.Deal_Amount ? inr(c.Deal_Amount) : "" },
          ],
          actions: actions(c),
        })}
      />
      {rejecting ? (
        <RejectModal
          item={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setNotice({ type: "success", text: "Commission rejected." });
            setRejecting(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
